/**
 * 통합관리자 — 환불 내역
 * ---------------------------------------------------------------------------
 * ## 왜 필요한가
 * 환불은 예전부터 사유와 승인자까지 기록되고 있었다. 그런데 **꺼내 보는
 * 화면이 없었다.** 수납 내역에 「환불액」 숫자 하나만 보였고, 왜 돌려줬는지
 * 알려면 감사로그를 뒤져야 했다. 돈을 돌려준 일을 설명할 수 없다는 뜻이다.
 *
 * ## 「갚았는가」가 가장 중요한 칸이다
 * 환불을 눌러도 고객 계좌로 돈을 보내지는 못한다. 다음 청구서에서 깎아
 * 갚는다. 그래서 운영자가 실제로 쫓아야 하는 숫자는 환불액이 아니라
 * <b>아직 못 갚은 금액</b>이다. 그 줄을 맨 위 요약과 표에 함께 둔다.
 *
 * 한 환불이 여러 달에 나뉘어 깎일 수 있어, 줄을 펼치면 **어느 청구서에서
 * 얼마가 깎였는지**가 보인다.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface RefundCredit {
  invoiceId: string;
  invoiceNo: string;
  amount: number;
  at: string;
}

interface RefundRow {
  refund_id: string;
  tenant_id: string;
  company_name: string;
  amount: number;
  credited_amount: number;
  pending_amount: number;
  reason: string;
  reason_code: string | null;
  status: string;
  requested_at: string;
  completed_at: string | null;
  approved_by_name: string | null;
  payment_id: string;
  method: string | null;
  payer_name: string | null;
  payment_amount: number;
  payment_at: string | null;
  invoice_id: string | null;
  invoice_no: string | null;
  credits: RefundCredit[];
}

interface RefundList {
  items: RefundRow[];
  summary: { total: number; credited: number; pending: number; cnt: number };
}

const won = (v: number | null | undefined) =>
  `${Math.round(Number(v ?? 0)).toLocaleString('ko-KR')}원`;
const ymd = (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD') : '-');

export function RefundPanel({ onOpenLedger }: { onOpenLedger: (tenantId: string) => void }) {
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [keyword, setKeyword] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);

  const params = {
    from: range?.[0]?.format('YYYY-MM-DD'),
    to: range?.[1]?.format('YYYY-MM-DD'),
    keyword: keyword.trim() || undefined,
    pendingOnly: pendingOnly ? 'true' : undefined,
    limit: 200,
  };

  const refunds = useQuery<RefundList, NormalizedError>({
    queryKey: ['admin', 'billing', 'refunds', params],
    queryFn: async () => (await apiClient.get('/admin/billing/refunds', { params })).data,
  });

  const columns: ColumnsType<RefundRow> = [
    {
      title: '환불일',
      key: 'at',
      width: 110,
      render: (_, r) => ymd(r.completed_at ?? r.requested_at),
    },
    {
      title: '기업',
      key: 'company',
      render: (_, r) => (
        <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}
          onClick={() => onOpenLedger(r.tenant_id)}>
          {r.company_name}
        </Button>
      ),
    },
    {
      title: '사유',
      dataIndex: 'reason',
      key: 'reason',
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            원 결제 {won(r.payment_amount)}
            {r.payer_name ? ` · ${r.payer_name}` : ''}
            {r.invoice_no ? ` · ${r.invoice_no}` : ''}
            {r.approved_by_name ? ` · 승인 ${r.approved_by_name}` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: '환불액',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (_, r) => <Text strong>{won(r.amount)}</Text>,
    },
    {
      title: '갚은 금액',
      key: 'credited',
      width: 110,
      align: 'right',
      render: (_, r) => won(r.credited_amount),
    },
    {
      /*
       * 이 화면의 핵심 칸이다. 0 원이면 다 갚은 것이고, 남아 있으면 다음
       * 청구서에서 그만큼 깎인다.
       */
      title: '아직 못 갚음',
      key: 'pending',
      width: 120,
      align: 'right',
      render: (_, r) =>
        r.pending_amount > 0 ? (
          <Text type="warning" strong>{won(r.pending_amount)}</Text>
        ) : (
          <Tag color="green">다 갚음</Tag>
        ),
    },
  ];

  const s = refunds.data?.summary;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="환불은 계좌로 보내지 않고 다음 청구서에서 깎아 드립니다"
        description={
          <>
            그래서 환불을 기록한 순간 그 금액은 <b>갚아야 할 돈</b>으로 남습니다. 다음 번
            청구서가 나갈 때 자동으로 차감되고, 청구 금액보다 크면 남는 만큼 그다음 달로
            넘어갑니다. 아래 <b>「아직 못 갚음」</b>이 0 원이 되면 끝난 것입니다.
          </>
        }
      />

      <Row gutter={12}>
        <Col span={6}>
          <Card size="small"><Statistic title="환불 건수" value={s?.cnt ?? 0} suffix="건" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small"><Statistic title="환불 총액" value={s?.total ?? 0} suffix="원" /></Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="청구서에서 갚은 금액" value={s?.credited ?? 0} suffix="원"
              valueStyle={{ color: '#3f8600' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="아직 못 갚은 금액" value={s?.pending ?? 0} suffix="원"
              valueStyle={{ color: (s?.pending ?? 0) > 0 ? '#d46b08' : undefined }} />
          </Card>
        </Col>
      </Row>

      <Space wrap align="center">
        <RangePicker
          value={range}
          onChange={(v) => setRange((v as [Dayjs, Dayjs] | null) ?? null)}
          allowEmpty={[true, true]}
        />
        <Input.Search
          allowClear
          placeholder="기업명 · 사유 · 입금자명"
          style={{ width: 260 }}
          onSearch={setKeyword}
        />
        <Space size={6}>
          <Switch size="small" checked={pendingOnly} onChange={setPendingOnly} />
          <Text>아직 못 갚은 것만</Text>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => void refunds.refetch()} />
      </Space>

      {refunds.isError && <Alert type="error" showIcon message={errorText(refunds.error)} />}

      <Table<RefundRow>
        rowKey="refund_id"
        size="small"
        loading={refunds.isLoading}
        dataSource={refunds.data?.items ?? []}
        columns={columns}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: '환불 내역이 없습니다.' }}
        expandable={{
          /* 한 환불이 여러 달에 나뉘어 깎일 수 있다. 그 내역을 펼쳐 보여 준다 */
          rowExpandable: (r) => (r.credits?.length ?? 0) > 0,
          expandedRowRender: (r) => (
            <Table
              rowKey="invoiceId"
              size="small"
              pagination={false}
              dataSource={r.credits}
              columns={[
                { title: '깎인 청구서', dataIndex: 'invoiceNo', key: 'no', width: 200 },
                {
                  title: '깎인 금액',
                  key: 'amt',
                  width: 130,
                  align: 'right',
                  render: (_: unknown, c: RefundCredit) => won(c.amount),
                },
                {
                  title: '반영일',
                  key: 'at',
                  render: (_: unknown, c: RefundCredit) => ymd(c.at),
                },
              ]}
            />
          ),
        }}
      />
    </Space>
  );
}
